import { useState } from 'react';
import { ArrowRight, MapPin, Package } from 'lucide-react';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { useMoveStore } from 'store/moveStore';
import { movePallet } from 'services/api/palletBuild';
import { playErrorSound, playGoodSound } from 'utils/sounds';

export const MoveConfirmPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { setCurrentScreen } = useAppStore();
  const { pallet, location, reset } = useMoveStore();

  const handleConfirm = async () => {
    if (!pallet || !location) return;
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await movePallet(pallet.pallet_id, location.location_id);
      if (data.success) {
        playGoodSound();
        reset();
        setCurrentScreen(SCREEN_TYPES.MOVE_SCAN);
      } else {
        setErrorMessage('Failed to move pallet. Please try again.');
        setHasError(true);
        playErrorSound();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentScreen(SCREEN_TYPES.MOVE_DESTINATION);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError ? 'text-white/80' : 'text-blue-500'
          )}
        >
          Move Pallet
        </span>
        <h2 className={cn('text-2xl font-bold', hasError ? 'text-white' : 'text-slate-800')}>
          Confirm Move
        </h2>
        {hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>}
      </div>

      <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">
            Transfer Details
          </span>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-5">
          <div className="flex flex-1 flex-col items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Pallet
            </span>
            <span className="font-mono text-base font-bold text-slate-800 text-center break-all">
              {pallet?.pallet_id ?? '—'}
            </span>
            {pallet && (
              <span className="text-xs text-slate-400">
                {pallet.carton_cnt} cases · {pallet.product_cnt} products
              </span>
            )}
          </div>

          <div className="flex flex-col items-center gap-1 shrink-0">
            <ArrowRight className="h-6 w-6 text-blue-400" />
          </div>

          <div className="flex flex-1 flex-col items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100">
              <MapPin className="h-5 w-5 text-emerald-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Destination
            </span>
            <span className="font-mono text-base font-bold text-slate-800 text-center break-all">
              {location?.location_id ?? '—'}
            </span>
            {location && (
              <span className="text-xs text-slate-400 capitalize">
                {location.type} · {location.status}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="buttons-container">
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          className="h-12 w-full rounded-2xl bg-emerald-500 text-base font-bold text-white shadow-md hover:bg-emerald-600 active:scale-[0.98]"
        >
          {isLoading ? 'Moving...' : 'Confirm'}
        </Button>

        <Button
          onClick={handleCancel}
          disabled={isLoading}
          variant="outline"
          className="h-12 w-full rounded-2xl border-2 border-rose-300 text-rose-500 text-base font-bold hover:bg-rose-50 hover:border-rose-400 hover:text-rose-600 active:scale-[0.98]"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
